// utils/errors/getBackendMessage.ts
export type BackendMessage = {
  text: string;
  type?: "error" | "success" | "warning" | "info";
  code?: string | number;
  status?: number;
  fieldErrors?: Record<string, string[]>;
  raw?: unknown;
};

function joinFieldErrors(obj: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      parts.push(`${k}: ${v.filter(Boolean).join(" | ")}`);
    } else if (typeof v === "string") {
      parts.push(`${k}: ${v}`);
    } else if (v && typeof v === "object") {
      // nested field errors
      parts.push(`${k}: ${joinFieldErrors(v as Record<string, unknown>)}`);
    }
  }
  return parts.join(" • ");
}

export default function getBackendMessage(err: unknown): BackendMessage {
  // Axios-style unwrap
  const data = (err as any)?.response?.data ?? (err as any)?.data ?? err;
  const status = 
    (err as any)?.response?.status ??
    (data?.status ?? undefined);

  // string error
  if (typeof data === "string") {
    return { text: data, type: "error", status, raw: err };
  }

  // Problem Details (RFC7807): { type, title, detail, status }
  if (data && typeof data === "object" && ("detail" in data || "title" in data)) {
    const d = data as Record<string, unknown>;
    const text =
      (typeof d.detail === "string" && d.detail) ||
      (typeof d.title === "string" && d.title) ||
      "An error occurred.";
    return {
      text,
      type: "error",
      status: typeof d.status === "number" ? d.status : status,
      code: typeof d.type === "string" ? d.type : undefined,
      raw: err,
    };
  }

  // DRF common: { detail }, or { message }, or { error }
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;

    if (typeof d.detail === "string") {
      return { text: d.detail, type: "error", status, raw: err };
    }
    if (typeof d.message === "string") {
      return { text: d.message, type: "error", status, raw: err };
    }
    if (typeof d.error === "string") {
      return { text: d.error, type: "error", status, raw: err };
    }

    // Field errors: { email: ["..."], password: ["..."], ... }
    const fieldLike = Object.values(d).some(
      (v) => Array.isArray(v) || (v && typeof v === "object")
    );
    if (fieldLike) {
      return {
        text: joinFieldErrors(d),
        type: "error",
        status,
        fieldErrors: Object.fromEntries(
          Object.entries(d).map(([k, v]) => [k, Array.isArray(v) ? v : [String(v)]])
        ),
        raw: err,
      };
    }
  }

  // Fallback for generic Error
  if (err instanceof Error && err.message) {
    return { text: err.message, type: "error", status, raw: err };
  }

  return { text: "An unexpected error occurred.", type: "error", status, raw: err };
}
